export type Language = "en" | "uk";

const english = {
  skip: "Skip to main content",
  navVisit: "Visit",
  navCommunity: "Parish life",
  navEvent: "Anniversary",
  navGallery: "Gallery",
  navContact: "Contact",
  utilityCurrent: "Service schedule",
  utilityVisit: "Visit us",
  utilityCommunity: "Parish life",
  utilityEvent: "Anniversary",
  utilityGallery: "Photos & video",
  utilityContact: "Contact",
  menuOpen: "Open menu",
  menuClose: "Close menu",
  languageLabel: "Language",
  heroGreeting: "Glory to Jesus Christ!",
  heroTitle: "A place to pray. A community to call home.",
  heroChurch: "Christ the Good Shepherd / St. Michael’s",
  heroDenomination: "Ukrainian Greek Catholic Church in Etobicoke",
  heroAddress: "182 Sixth Street · Etobicoke, Ontario",
  heroDirections: "Get directions",
  heroSchedule: "Current information",
  heroScroll: "Discover our parish",
  visitEyebrow: "Welcome",
  visitTitle: "Welcome to our parish",
  visitBody: "Whether you have worshipped in the Ukrainian Catholic tradition all your life or are walking through our doors for the first time, there is a place for you here.",
  visitScheduleLabel: "Current information",
  visitScheduleBody: "Our schedule is shared through the parish’s Facebook and Instagram pages. Check the latest post before your visit, or send us a message through social media.",
  visitAddressLabel: "Our address",
  visitAddress: "182 Sixth St, Etobicoke, ON M8V 3A5",
  visitMap: "Open in Google Maps",
  visitInstagram: "See the latest on Instagram",
  visitFacebook: "Visit our Facebook page",
  communityEyebrow: "Parish life",
  communityTitle: "Worship and community",
  communityBody: "Our parish gathers generations around the Divine Liturgy, Ukrainian spiritual tradition, and the life of our neighbourhood.",
  communityPullquote: "The Church is most beautiful when every generation has a place within it.",
  communityChildrenLabel: "Children & families",
  communityChildrenText: "Faith is passed on through prayer, song, celebration, and the patient care of a community.",
  communityWorshipLabel: "Worship & tradition",
  communityWorshipText: "The beauty of the Eastern Christian tradition draws our attention toward prayer and the presence of God.",
  communityTogetherLabel: "Community",
  communityTogetherText: "Parishioners gather throughout the year for worship, celebrations, music, and Ukrainian traditions.",
  clergyEyebrow: "25 years of priestly ministry",
  clergyTitle: "Fr. Andrij Figol",
  clergyBody: "This autumn our community gathers to celebrate a milestone of faithful service and give thanks for twenty-five years of priestly ministry.",
  eventEyebrow: "Saturday · October 3, 2026",
  eventTitle: "Fr. Andrij Figol’s 25th anniversary of ordination",
  eventBody: "An evening of gratitude, fellowship, and celebration in the Christ the Good Shepherd / St. Michael’s Church Hall.",
  eventDoorsLabel: "Doors open",
  eventDoors: "5:00 PM",
  eventDinnerLabel: "Dinner",
  eventDinner: "6:00 PM",
  eventPriceLabel: "Tickets",
  eventPrice: "$90 per person · cash bar",
  eventVenueLabel: "Location",
  eventVenue: "182 Sixth Street, Etobicoke, ON",
  eventTicketsLabel: "Ticket contacts",
  eventTatjana: "Tatjana · (647) 205-0672",
  eventOksana: "Oksana · (437) 299-4896",
  eventEmailLabel: "Email Tatjana",
  eventFlyerAlt: "Bilingual invitation to Fr. Andrij Figol’s 25th anniversary of ordination to the priesthood on October 3, 2026",
  galleryEyebrow: "Parish gallery",
  galleryTitle: "Photos and videos from parish life",
  galleryBody: "Scenes from worship, feast days, children’s ministry, and parish celebrations.",
  galleryPhoto: "Photograph",
  galleryVideo: "Video",
  galleryOpen: "Open media",
  galleryPlay: "Play video",
  dialogClose: "Close",
  dialogPrevious: "Previous",
  dialogNext: "Next",
  contactEyebrow: "Contact",
  contactTitle: "Visit Christ the Good Shepherd / St. Michael’s",
  contactBody: "Find us one block north of Lake Shore Boulevard West in New Toronto. For current services and parish announcements, follow our social pages.",
  contactDirections: "Directions to the church",
  contactInstagram: "Instagram",
  contactFacebook: "Facebook",
  footerChurch: "Christ the Good Shepherd / St. Michael’s Ukrainian Greek Catholic Church",
  footerAddress: "182 Sixth St, Etobicoke, ON M8V 3A5",
  footerRights: "Parish information website",
  brandName: "Christ the Good Shepherd / St. Michael’s",
  brandAddress: "182 Sixth St, Etobicoke, ON",
  heroImageLabel: "Inside our church",
  heroAddressFull: "182 Sixth St, Etobicoke, ON M8V 3A5",
  scheduleTitle: "Service schedule",
  scheduleLiturgyLabel: "Divine Liturgy",
  scheduleLiturgyTimes: "See the latest post on our Facebook or Instagram",
  scheduleOfficeLabel: "Parish office",
  scheduleOfficeHours: "Monday–Friday, 10:00 AM – 5:00 PM",
  footerOffice: "Parish office: Monday–Friday, 10:00 AM – 5:00 PM",
} as const;

export type ContentKey = keyof typeof english;

const ukrainian: Record<ContentKey, string> = {
  skip: "Перейти до основного вмісту",
  navVisit: "Відвідати",
  navCommunity: "Життя парафії",
  navEvent: "Ювілей",
  navGallery: "Галерея",
  navContact: "Контакти",
  utilityCurrent: "Розклад богослужінь",
  utilityVisit: "Відвідати нас",
  utilityCommunity: "Життя парафії",
  utilityEvent: "Ювілей",
  utilityGallery: "Фото та відео",
  utilityContact: "Контакти",
  menuOpen: "Відкрити меню",
  menuClose: "Закрити меню",
  languageLabel: "Мова",
  heroGreeting: "Слава Ісусу Христу!",
  heroTitle: "Місце для молитви. Спільнота, яку можна назвати домом.",
  heroChurch: "Христа Доброго Пастиря / Святого Архангела Михаїла",
  heroDenomination: "Українська греко-католицька церква в Етобіко",
  heroAddress: "182 Sixth Street · Етобіко, Онтаріо",
  heroDirections: "Прокласти маршрут",
  heroSchedule: "Актуальна інформація",
  heroScroll: "Відкрийте нашу парафію",
  visitEyebrow: "Ласкаво просимо",
  visitTitle: "Ласкаво просимо до нашої парафії",
  visitBody: "Чи ви все життя молитеся в українській католицькій традиції, чи вперше переступаєте поріг нашого храму — тут є місце для вас.",
  visitScheduleLabel: "Актуальна інформація",
  visitScheduleBody: "Розклад публікуємо на сторінках парафії у Facebook та Instagram. Перед візитом перегляньте останній допис або напишіть нам у соціальних мережах.",
  visitAddressLabel: "Наша адреса",
  visitAddress: "182 Sixth St, Etobicoke, ON M8V 3A5",
  visitMap: "Відкрити в Google Maps",
  visitInstagram: "Останні новини в Instagram",
  visitFacebook: "Наша сторінка у Facebook",
  communityEyebrow: "Життя парафії",
  communityTitle: "Богослужіння та спільнота",
  communityBody: "Наша парафія єднає покоління навколо Божественної Літургії, української духовної традиції та життя нашого району.",
  communityPullquote: "Церква найпрекрасніша тоді, коли в ній є місце для кожного покоління.",
  communityChildrenLabel: "Діти та родини",
  communityChildrenText: "Віра передається через молитву, пісню, свято та дбайливу присутність спільноти.",
  communityWorshipLabel: "Богослужіння та традиція",
  communityWorshipText: "Краса східнохристиянської традиції спрямовує нашу увагу до молитви й Божої присутності.",
  communityTogetherLabel: "Спільнота",
  communityTogetherText: "Парафіяни збираються протягом року на богослужіння, святкування, спів та українські традиції.",
  clergyEyebrow: "25 років священничого служіння",
  clergyTitle: "о. Андрій Фіґоль",
  clergyBody: "Цієї осені наша громада збирається, щоб відзначити важливу подію вірного служіння та подякувати за двадцять п’ять років священства.",
  eventEyebrow: "Субота · 3 жовтня 2026 року",
  eventTitle: "Запрошуємо відзначити 25-ліття священничих свячень о. Андрія Фіґоля.",
  eventBody: "Вечір вдячності, спілкування та святкування у церковній залі Христа Доброго Пастиря / Святого Архангела Михаїла.",
  eventDoorsLabel: "Відкриття дверей",
  eventDoors: "17:00",
  eventDinnerLabel: "Вечеря",
  eventDinner: "18:00",
  eventPriceLabel: "Квитки",
  eventPrice: "$90 з особи · платний бар",
  eventVenueLabel: "Місце",
  eventVenue: "182 Sixth Street, Etobicoke, ON",
  eventTicketsLabel: "Контакти для придбання квитків",
  eventTatjana: "Тетяна · (647) 205-0672",
  eventOksana: "Оксана · (437) 299-4896",
  eventEmailLabel: "Написати Тетяні",
  eventFlyerAlt: "Двомовне запрошення на святкування 25-ліття священничих свячень о. Андрія Фіґоля 3 жовтня 2026 року",
  galleryEyebrow: "Парафіяльна галерея",
  galleryTitle: "Фото та відео з життя парафії",
  galleryBody: "Богослужіння, свята, дитяче служіння та парафіяльні зустрічі.",
  galleryPhoto: "Світлина",
  galleryVideo: "Відео",
  galleryOpen: "Відкрити медіа",
  galleryPlay: "Відтворити відео",
  dialogClose: "Закрити",
  dialogPrevious: "Попереднє",
  dialogNext: "Наступне",
  contactEyebrow: "Контакти",
  contactTitle: "Відвідайте церкву Христа Доброго Пастиря / Святого Архангела Михаїла",
  contactBody: "Ми розташовані за один квартал на північ від Lake Shore Boulevard West у районі New Toronto. Актуальний розклад і парафіяльні оголошення шукайте на наших сторінках у соцмережах.",
  contactDirections: "Маршрут до церкви",
  contactInstagram: "Instagram",
  contactFacebook: "Facebook",
  footerChurch: "Українська греко-католицька церква Христа Доброго Пастиря / Святого Архангела Михаїла",
  footerAddress: "182 Sixth St, Etobicoke, ON M8V 3A5",
  footerRights: "Інформаційний сайт парафії",
  brandName: "Христа Доброго Пастиря / Св. Архангела Михаїла",
  brandAddress: "182 Sixth St, Етобіко, Онтаріо",
  heroImageLabel: "Інтер’єр нашої церкви",
  heroAddressFull: "182 Sixth St, Етобіко, Онтаріо M8V 3A5",
  scheduleTitle: "Розклад богослужінь",
  scheduleLiturgyLabel: "Божественна Літургія",
  scheduleLiturgyTimes: "Дивіться останній допис на нашій сторінці у Facebook або Instagram",
  scheduleOfficeLabel: "Парафіяльна канцелярія",
  scheduleOfficeHours: "Понеділок–пʼятниця, 10:00–17:00",
  footerOffice: "Парафіяльна канцелярія: понеділок–пʼятниця, 10:00–17:00",
};

export const copy: Record<Language, Record<ContentKey, string>> = {
  en: english,
  uk: ukrainian,
};

const metadata = {
  en: {
    title: "Christ the Good Shepherd / St. Michael’s UGCC | Etobicoke",
    description: "A welcoming Ukrainian Greek Catholic parish at 182 Sixth Street in Etobicoke, Ontario. Visit, connect, and discover our parish community.",
  },
  uk: {
    title: "Церква Христа Доброго Пастиря / Св. Архангела Михаїла | Етобіко",
    description: "Українська греко-католицька парафія за адресою 182 Sixth Street в Етобіко, Онтаріо. Завітайте та познайомтеся з нашою громадою.",
  },
} satisfies Record<Language, { title: string; description: string }>;

export function getInitialLanguage(): Language {
  const query = new URLSearchParams(window.location.search).get("lang");
  if (query === "uk" || query === "en") return query;

  try {
    const saved = window.localStorage.getItem("parish-language");
    if (saved === "uk" || saved === "en") return saved;
  } catch {
    // English remains the resilient fallback when storage is unavailable.
  }

  return "en";
}

export function applyLanguage(language: Language): void {
  document.documentElement.lang = language;
  document.title = metadata[language].title;

  document.querySelectorAll<HTMLElement>("[data-i18n]").forEach((element) => {
    const key = element.dataset.i18n as ContentKey | undefined;
    if (key && copy[language][key]) element.textContent = copy[language][key];
  });

  document.querySelectorAll<HTMLElement>("[data-i18n-aria]").forEach((element) => {
    const key = element.dataset.i18nAria as ContentKey | undefined;
    if (key && copy[language][key]) element.setAttribute("aria-label", copy[language][key]);
  });

  document.querySelectorAll<HTMLButtonElement>("[data-language]").forEach((button) => {
    const active = button.dataset.language === language;
    button.setAttribute("aria-pressed", String(active));
  });

  const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
  const ogTitle = document.querySelector<HTMLMetaElement>('meta[property="og:title"]');
  const ogDescription = document.querySelector<HTMLMetaElement>('meta[property="og:description"]');
  if (description) description.content = metadata[language].description;
  if (ogTitle) ogTitle.content = metadata[language].title;
  if (ogDescription) ogDescription.content = metadata[language].description;

  try {
    window.localStorage.setItem("parish-language", language);
  } catch {
    // Language still changes for the current page when storage is unavailable.
  }

  window.dispatchEvent(new CustomEvent<Language>("parish-language-change", { detail: language }));
}
